import React, { useState, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";
import {
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Box,
  InputAdornment,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
} from "@mui/material";
import {
  Search,
  DirectionsCar,
  AccessTime,
  ExitToApp,
  CheckCircle,
  Schedule,
  LocationOn,
  DoorFront,
  Close,
  Visibility,
  FilterList,
  Refresh as RefreshIcon,
  Clear,
  Info,
  Add,
  Download as DownloadIcon,
} from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import MDInput from "shared/components/MDInput";
import DataTable from "shared/components/Tables/DataTable";
import {
  formatDate,
  calculateDuration,
  filterRecordsByDateRange,
  getFilterStats,
} from "modules/copropiedades/services/semestrales";
import CloseCycleDialog from "../modal/CloseCycleDialog";
import CreateCycleDialog from "../modal/CreateCycleDialog";
import CycleSuccessDialog from "../modal/CycleSuccessDialog";

const TABLE_ENTRIES = { defaultValue: 15, entries: [5, 10, 15, 20, 25] };

const TableSemestrales = forwardRef(
  (
    {
      records = [],
      loading = false,
      error = null,
      onRefresh = null,
      onCloseCycle = null,
      onAddCycle = null,
      terminales = null,
      hasCreatePermission = false,
      hasClosePermission = false,
    },
    ref
  ) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [statusMessage, setStatusMessage] = useState("");

    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [createStatus, setCreateStatus] = useState(null);
    const [createMessage, setCreateMessage] = useState("");

    const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const [successType, setSuccessType] = useState("create");

    const [isExporting, setIsExporting] = useState(false);

    // Estados para el filtro de fechas
    const [dateRange, setDateRange] = useState({
      startDate: "",
      endDate: "",
    });

    const isDateFilterActive = dateRange.startDate !== "" || dateRange.endDate !== "";

    // Función para formatear fecha para mostrar en el filtro
    const formatDateDisplay = (dateString) => {
      if (!dateString) return "";

      const parts = dateString.split("-");
      if (parts.length === 3) {
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1;
        const day = parseInt(parts[2]);
        const date = new Date(Date.UTC(year, month, day));
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          timeZone: "UTC",
        });
      }
      return dateString;
    };

    const columns = [
      { Header: "#", accessor: "index", width: "5%" },
      { Header: "Placa", accessor: "plate", width: "12%" },
      { Header: "Host / Cámara", accessor: "host", width: "12%" },
      { Header: "ID", accessor: "id", width: "8%" },
      { Header: "Entrada", accessor: "enter_at", width: "13%" },
      { Header: "Salida", accessor: "out_at", width: "13%" },
      { Header: "Duración", accessor: "duration", width: "10%" },
      { Header: "Estado", accessor: "status", width: "10%" },
      { Header: "Acciones", accessor: "actions", width: "17%" },
    ];

    const renderPlate = (plate) => (
      <Box display="flex" alignItems="center" gap={1}>
        <DirectionsCar fontSize="small" color="primary" />
        <MDTypography variant="body1" fontWeight="bold" sx={{ fontSize: "0.875rem" }}>
          {plate || "Sin placa"}
        </MDTypography>
      </Box>
    );

    const renderDateTime = (dateTime, icon = null) => {
      if (!dateTime) {
        return (
          <MDTypography variant="body2" color="text.secondary">
            ---
          </MDTypography>
        );
      }
      return (
        <Box display="flex" alignItems="center" gap={1}>
          {icon || <AccessTime fontSize="small" color="action" />}
          <MDTypography variant="body2">{formatDate(dateTime)}</MDTypography>
        </Box>
      );
    };

    const renderStatus = (record) => {
      const isActive = record.out_at === null;
      return (
        <Chip
          label={isActive ? "ACTIVO" : "CICLO CERRADO"}
          size="small"
          color={isActive ? "warning" : "success"}
          icon={isActive ? <Schedule /> : <CheckCircle />}
          sx={{ fontWeight: "bold", fontSize: "0.7rem", height: "26px" }}
        />
      );
    };

    const renderActions = (record) => {
      const isActive = record.out_at === null;

      return (
        <Box display="flex" gap={1}>
          {isActive ? (
            hasClosePermission ? (
              <Tooltip title="Cerrar ciclo (registrar salida)">
                <MDButton
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={() => handleCloseCycleClick(record)}
                  startIcon={<ExitToApp />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                    minWidth: "120px",
                    height: "32px",
                    boxShadow: "0 2px 8px rgba(237, 108, 2, 0.2)",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(237, 108, 2, 0.3)",
                    },
                  }}
                >
                  Cerrar Ciclo
                </MDButton>
              </Tooltip>
            ) : (
              <Chip
                label="ACTIVO"
                size="small"
                color="warning"
                icon={<Schedule />}
                sx={{ fontWeight: "bold", fontSize: "0.7rem", height: "26px" }}
              />
            )
          ) : (
            <Tooltip title="Ciclo completado">
              <Chip
                label="COMPLETADO"
                size="small"
                color="success"
                icon={<CheckCircle />}
                sx={{ fontWeight: "bold", fontSize: "0.7rem", height: "26px" }}
              />
            </Tooltip>
          )}
        </Box>
      );
    };

    const handleCloseCycleClick = (record) => {
      setSelectedRecord(record);
      setOpenDialog(true);
      setSubmitStatus(null);
      setStatusMessage("");
      setIsSubmitting(false);
    };

    const handleDialogClose = () => {
      if (isSubmitting) return;
      setOpenDialog(false);
      setSelectedRecord(null);
      setSubmitStatus(null);
      setStatusMessage("");
      setIsSubmitting(false);
    };

    const handleDialogConfirm = async (observation, exitDate) => {
      setIsSubmitting(true);
      setSubmitStatus(null);
      setStatusMessage("Cerrando ciclo...");

      try {
        const result = await onCloseCycle(
          selectedRecord.id,
          selectedRecord.plate,
          exitDate,
          observation
        );
        if (result && result.success) {
          setSubmitStatus("success");
          setStatusMessage("Ciclo cerrado exitosamente");
          setSuccessData({
            plate: selectedRecord?.plate,
            observation: observation,
            exitDate: exitDate,
            out_at: exitDate,
            ...result.data,
          });
          setSuccessType("close");
          handleDialogClose();
          setTimeout(() => {
            setOpenSuccessDialog(true);
          }, 300);
        } else {
          setSubmitStatus("error");
          setStatusMessage(result?.error || "Error al cerrar el ciclo");
        }
      } catch (error) {
        setSubmitStatus("error");
        setStatusMessage("Error al cerrar el ciclo");
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleCreateConfirm = async (data) => {
      setIsCreating(true);
      setCreateStatus(null);
      setCreateMessage("Creando ciclo...");

      try {
        const result = await onAddCycle(data.plate, data.date, data.observation);
        if (result && result.success) {
          setCreateStatus("success");
          setCreateMessage("Ciclo creado exitosamente");
          setSuccessData({
            plate: data.plate,
            enter_at: data.date,
            observation: data.observation,
            ...result.data,
          });
          setSuccessType("create");
          setOpenCreateDialog(false);
          setCreateStatus(null);
          setCreateMessage("");
          setTimeout(() => {
            setOpenSuccessDialog(true);
          }, 300);
        } else {
          setCreateStatus("error");
          setCreateMessage(result?.error || "Error al crear el ciclo");
        }
      } catch (error) {
        setCreateStatus("error");
        setCreateMessage("Error al crear el ciclo");
      } finally {
        setIsCreating(false);
      }
    };

    const handleSuccessDialogClose = () => {
      setOpenSuccessDialog(false);
      setSuccessData(null);
      if (onRefresh) {
        onRefresh();
      }
    };

    const handleFilterChange = (event, newFilterType) => {
      if (newFilterType !== null) {
        setFilterType(newFilterType);
      }
    };

    const resetFilters = () => {
      setSearchQuery("");
      setFilterType("all");
      setDateRange({ startDate: "", endDate: "" });
    };

    const clearDateFilter = () => {
      setDateRange({ startDate: "", endDate: "" });
    };

    const handleDateChange = (field, value) => {
      setDateRange((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const getFilteredAndSortedRecords = () => {
      let sorted = [...records].sort((a, b) => {
        const dateA = new Date(a.enter_at || 0);
        const dateB = new Date(b.enter_at || 0);
        return dateB - dateA;
      });

      if (dateRange.startDate || dateRange.endDate) {
        sorted = filterRecordsByDateRange(
          sorted,
          dateRange.startDate || null,
          dateRange.endDate || null
        );
      }

      if (filterType === "active") {
        sorted = sorted.filter((record) => record.out_at === null);
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        sorted = sorted.filter((record) => {
          const searchableFields = [
            record.id?.toString().toLowerCase() || "",
            record.plate?.toLowerCase() || "",
            record.host?.toLowerCase() || "",
            record.enter_at ? formatDate(record.enter_at).toLowerCase() : "",
            record.out_at ? formatDate(record.out_at).toLowerCase() : "",
          ];
          return searchableFields.some((field) => field.includes(query));
        });
      }

      return sorted;
    };

    const filteredData = getFilteredAndSortedRecords();
    const activeCount = records.filter((r) => r.out_at === null).length;
    const filterStats = isDateFilterActive
      ? getFilterStats(records, dateRange.startDate || null, dateRange.endDate || null)
      : null;

    // ============================================================
    // FUNCIÓN DE EXPORTACIÓN A EXCEL
    // ============================================================
    const handleExportExcel = () => {
      try {
        setIsExporting(true);

        if (!filteredData || filteredData.length === 0) {
          alert("No hay datos para exportar");
          setIsExporting(false);
          return;
        }

        // Construimos los datos con los mismos campos que se muestran en la tabla
        const excelData = filteredData.map((record, index) => ({
          "#": index + 1,
          Placa: record.plate || "Sin placa",
          "Host / Cámara": record.host || "N/A",
          ID: record.id || "—",
          Entrada: record.enter_at ? formatDate(record.enter_at) : "---",
          Salida: record.out_at ? formatDate(record.out_at) : "---",
          Duración: record.out_at
            ? calculateDuration(record.enter_at, record.out_at)
            : calculateDuration(record.enter_at),
          Estado: record.out_at === null ? "ACTIVO" : "CICLO CERRADO",
        }));

        // Creamos la hoja de cálculo
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Ajustamos el ancho de las columnas
        worksheet["!cols"] = [
          { wch: 6 }, // #
          { wch: 15 }, // Placa
          { wch: 22 }, // Host / Cámara
          { wch: 12 }, // ID
          { wch: 22 }, // Entrada
          { wch: 22 }, // Salida
          { wch: 18 }, // Duración
          { wch: 18 }, // Estado
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Semestrales");

        // Nombre del archivo con fecha
        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(today.getDate()).padStart(2, "0")}`;

        let fileName = `semestrales_${dateStr}`;
        if (dateRange.startDate && dateRange.endDate) {
          fileName = `semestrales_${dateRange.startDate}_a_${dateRange.endDate}`;
        } else if (dateRange.startDate) {
          fileName = `semestrales_desde_${dateRange.startDate}`;
        } else if (dateRange.endDate) {
          fileName = `semestrales_hasta_${dateRange.endDate}`;
        } else if (filterType === "active") {
          fileName = `semestrales_activos_${dateStr}`;
        }

        XLSX.writeFile(workbook, `${fileName}.xlsx`);
      } catch (err) {
        console.error("Error al exportar a Excel:", err);
        alert("Ocurrió un error al generar el archivo Excel");
      } finally {
        setIsExporting(false);
      }
    };

    const rows = filteredData.map((record, index) => ({
      id: (
        <MDTypography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: "1rem",
            fontWeight: 500,
            fontFamily: "monospace",
          }}
        >
          {record.id || "—"}
        </MDTypography>
      ),
      index: (
        <MDTypography variant="body2" color="text.secondary">
          {index + 1}
        </MDTypography>
      ),
      plate: renderPlate(record.plate),
      host: (
        <Box display="flex" alignItems="center" gap={1}>
          <LocationOn fontSize="small" color="info" />
          <MDTypography variant="body2">{record.host || "N/A"}</MDTypography>
        </Box>
      ),
      enter_at: renderDateTime(record.enter_at, <DoorFront fontSize="small" color="success" />),
      out_at: renderDateTime(record.out_at, <ExitToApp fontSize="small" color="error" />),
      duration: (
        <Box display="flex" alignItems="center" gap={1}>
          <AccessTime fontSize="small" color="action" />
          <MDTypography
            variant="body2"
            fontWeight={record.out_at ? "medium" : "bold"}
            color={record.out_at ? "text.primary" : "warning.main"}
          >
            {record.out_at
              ? calculateDuration(record.enter_at, record.out_at)
              : calculateDuration(record.enter_at)}
          </MDTypography>
        </Box>
      ),
      status: renderStatus(record),
      actions: renderActions(record),
    }));

    const handleSearchChange = (e) => {
      setSearchQuery(e.target.value);
    };

    const clearSearch = () => {
      setSearchQuery("");
    };

    useImperativeHandle(ref, () => ({
      openCreateDialog: () => {
        if (hasCreatePermission) {
          setOpenCreateDialog(true);
          setCreateStatus(null);
          setCreateMessage("");
          setIsCreating(false);
        }
      },
    }));

    if (loading && records.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Box textAlign="center">
            <CircularProgress color="primary" size={60} />
            <MDTypography variant="h5" mt={2}>
              Cargando registros...
            </MDTypography>
          </Box>
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={3}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
            <MDTypography variant="h6" fontWeight="bold">
              Error al cargar datos
            </MDTypography>
            <MDTypography variant="body1">{error}</MDTypography>
          </Alert>
          <Box display="flex" justifyContent="center" mt={2}>
            <MDButton variant="contained" color="primary" onClick={onRefresh}>
              Reintentar carga
            </MDButton>
          </Box>
        </Box>
      );
    }

    // Cuando no hay datos filtrados, mostrar la tabla vacía
    if (filteredData.length === 0) {
      const hasFilters = searchQuery || filterType !== "all" || isDateFilterActive;

      const renderFilters = () => (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            backgroundColor: "#f8f9fa",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box sx={{ flex: 1, minWidth: "300px", display: "flex", gap: 1 }}>
              <MDInput
                fullWidth
                placeholder="Buscar por ID, placa, host o fecha..."
                value={searchQuery}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#9e9e9e", fontSize: "1.2rem" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <Close fontSize="small" sx={{ color: "#9e9e9e" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0d47a1",
                    },
                  },
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "48px",
                    fontSize: "1rem",
                  },
                }}
              />

              <TextField
                label="Desde"
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleDateChange("startDate", e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: dateRange.endDate || undefined,
                }}
                sx={{
                  minWidth: "200px",
                  width: "220px",
                  "& .MuiInputBase-root": {
                    height: "48px",
                    fontSize: "1rem",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.9rem",
                    top: "-6px",
                    fontWeight: 500,
                  },
                }}
              />

              <TextField
                label="Hasta"
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleDateChange("endDate", e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: dateRange.startDate || undefined,
                }}
                sx={{
                  minWidth: "200px",
                  width: "220px",
                  "& .MuiInputBase-root": {
                    height: "48px",
                    fontSize: "1rem",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.9rem",
                    top: "-6px",
                    fontWeight: 500,
                  },
                }}
              />

              {isDateFilterActive && (
                <Tooltip title="Limpiar filtro de fechas">
                  <IconButton
                    size="small"
                    onClick={clearDateFilter}
                    sx={{
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                      backgroundColor: "white",
                      height: "48px",
                      width: "48px",
                      "&:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.04)",
                        borderColor: "#d32f2f",
                      },
                    }}
                  >
                    <Clear sx={{ color: "#d32f2f", fontSize: "1.4rem" }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <FilterList sx={{ color: "#757575", fontSize: "1.1rem" }} />
              <ToggleButtonGroup
                value={filterType}
                exclusive
                onChange={handleFilterChange}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    px: 2.5,
                    py: 0.6,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "white",
                    color: "#757575",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(13, 71, 161, 0.04)",
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#0d47a1",
                      color: "white !important",
                      borderColor: "#0d47a1",
                      boxShadow: "0 2px 8px rgba(13, 71, 161, 0.2)",
                      "&:hover": {
                        backgroundColor: "#1565c0",
                        color: "white !important",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "white !important",
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="all">
                  <Visibility fontSize="small" sx={{ mr: 0.8, fontSize: "1rem" }} />
                  Todos ({records.length})
                </ToggleButton>
                <ToggleButton value="active">
                  <Schedule fontSize="small" sx={{ mr: 0.8, fontSize: "1rem" }} />
                  Activos ({activeCount})
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {isDateFilterActive && filterStats && (
            <Box
              mt={2}
              pt={2}
              borderTop="1px solid #e0e0e0"
              display="flex"
              alignItems="center"
              gap={3}
              flexWrap="wrap"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Info fontSize="small" color="info" />
                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                  Filtro aplicado:
                </Typography>
              </Box>
              <Chip
                label={`Total: ${filterStats.total}`}
                size="small"
                color="default"
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Chip
                label={`Activos: ${filterStats.active}`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Chip
                label={`Completados: ${filterStats.completed}`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Chip
                label={`Placas únicas: ${filterStats.uniquePlates}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: "medium" }}>
                {dateRange.startDate && dateRange.endDate
                  ? `Del ${formatDateDisplay(dateRange.startDate)} al ${formatDateDisplay(
                      dateRange.endDate
                    )}`
                  : dateRange.startDate
                  ? `Desde ${formatDateDisplay(dateRange.startDate)}`
                  : `Hasta ${formatDateDisplay(dateRange.endDate)}`}
              </Typography>
            </Box>
          )}
        </Paper>
      );

      return (
        <Box>
          {renderFilters()}

          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid #e0e0e0",
            }}
          >
            <DataTable
              table={{ columns, rows: [] }}
              isSorted={false}
              entriesPerPage={TABLE_ENTRIES}
              autoResetPage={false}
              paginationResetKey={JSON.stringify([
                searchQuery,
                filterType,
                dateRange.startDate,
                dateRange.endDate,
              ])}
              showTotalEntries={true}
              noEndBorder={true}
              canSearch={false}
              pagination={{ variant: "gradient", color: "primary" }}
              sx={{
                "& .MuiTableRow-root": {
                  transition: "background-color 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(13, 71, 161, 0.02)",
                  },
                },
                "& .MuiTableCell-head": {
                  backgroundColor: "#f5f7fa",
                  fontWeight: 600,
                  color: "#424242",
                  borderBottom: "2px solid #e0e0e0",
                },
              }}
            />

            <Box
              sx={{
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fafafa",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <Search sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
              <MDTypography variant="h6" fontWeight="bold" gutterBottom color="text.secondary">
                {hasFilters ? "No se encontraron resultados" : "No hay registros"}
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                {hasFilters
                  ? `No hay registros que coincidan con los filtros aplicados.`
                  : "No se encontraron registros en el sistema."}
              </MDTypography>

              <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
                {hasFilters && (
                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={resetFilters}
                    startIcon={<RefreshIcon />}
                    sx={{ borderRadius: "8px" }}
                  >
                    Mostrar Todos
                  </MDButton>
                )}

                {hasCreatePermission && (
                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setOpenCreateDialog(true);
                      setCreateStatus(null);
                      setCreateMessage("");
                      setIsCreating(false);
                    }}
                    startIcon={<Add />}
                    sx={{
                      borderRadius: "8px",
                      backgroundColor: "#0d47a1",
                      "&:hover": {
                        backgroundColor: "#1565c0",
                      },
                    }}
                  >
                    Crear Ciclo
                  </MDButton>
                )}

                {!hasFilters && (
                  <MDButton
                    variant="outlined"
                    color="primary"
                    onClick={onRefresh}
                    startIcon={<RefreshIcon />}
                    sx={{ borderRadius: "8px" }}
                  >
                    Actualizar
                  </MDButton>
                )}
              </Box>
            </Box>
          </Paper>

          <CloseCycleDialog
            open={openDialog}
            onClose={handleDialogClose}
            onConfirm={handleDialogConfirm}
            selectedRecord={selectedRecord}
            isSubmitting={isSubmitting}
            submitStatus={submitStatus}
            statusMessage={statusMessage}
          />

          <CreateCycleDialog
            open={openCreateDialog}
            onClose={() => {
              if (!isCreating) {
                setOpenCreateDialog(false);
                setCreateStatus(null);
                setCreateMessage("");
              }
            }}
            onConfirm={handleCreateConfirm}
            isSubmitting={isCreating}
            submitStatus={createStatus}
            statusMessage={createMessage}
            terminalInfo={terminales?.entrada || null}
          />

          <CycleSuccessDialog
            open={openSuccessDialog}
            onClose={handleSuccessDialogClose}
            data={successData}
            type={successType}
            autoClose={true}
            autoCloseDelay={3000}
          />
        </Box>
      );
    }

    // Conservar el paginador montado mientras se actualizan registros existentes.
    return (
      <Box sx={{ position: "relative" }} aria-busy={loading}>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              backgroundColor: "rgba(255,255,255,0.65)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress aria-label="Actualizando registros" />
          </Box>
        )}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            backgroundColor: "#f8f9fa",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box sx={{ flex: 1, minWidth: "300px", display: "flex", gap: 1 }}>
              <MDInput
                fullWidth
                placeholder="Buscar por ID, placa, host o fecha..."
                value={searchQuery}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#9e9e9e", fontSize: "1.2rem" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <Close fontSize="small" sx={{ color: "#9e9e9e" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0d47a1",
                    },
                  },
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "48px",
                    fontSize: "1rem",
                  },
                }}
              />

              <TextField
                label="Desde"
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleDateChange("startDate", e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: dateRange.endDate || undefined,
                }}
                sx={{
                  minWidth: "200px",
                  width: "220px",
                  "& .MuiInputBase-root": {
                    height: "48px",
                    fontSize: "1rem",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.9rem",
                    top: "-6px",
                    fontWeight: 500,
                  },
                }}
              />

              <TextField
                label="Hasta"
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleDateChange("endDate", e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: dateRange.startDate || undefined,
                }}
                sx={{
                  minWidth: "200px",
                  width: "220px",
                  "& .MuiInputBase-root": {
                    height: "48px",
                    fontSize: "1rem",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.9rem",
                    top: "-6px",
                    fontWeight: 500,
                  },
                }}
              />

              {isDateFilterActive && (
                <Tooltip title="Limpiar filtro de fechas">
                  <IconButton
                    size="small"
                    onClick={clearDateFilter}
                    sx={{
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                      backgroundColor: "white",
                      height: "48px",
                      width: "48px",
                      "&:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.04)",
                        borderColor: "#d32f2f",
                      },
                    }}
                  >
                    <Clear sx={{ color: "#d32f2f", fontSize: "1.4rem" }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <FilterList sx={{ color: "#757575", fontSize: "1.1rem" }} />
              <ToggleButtonGroup
                value={filterType}
                exclusive
                onChange={handleFilterChange}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    px: 2.5,
                    py: 0.6,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "white",
                    color: "#757575",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(13, 71, 161, 0.04)",
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#0d47a1",
                      color: "white !important",
                      borderColor: "#0d47a1",
                      boxShadow: "0 2px 8px rgba(13, 71, 161, 0.2)",
                      "&:hover": {
                        backgroundColor: "#1565c0",
                        color: "white !important",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "white !important",
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="all">
                  <Visibility fontSize="small" sx={{ mr: 0.8, fontSize: "1rem" }} />
                  Todos ({records.length})
                </ToggleButton>
                <ToggleButton value="active">
                  <Schedule fontSize="small" sx={{ mr: 0.8, fontSize: "1rem" }} />
                  Activos ({activeCount})
                </ToggleButton>
              </ToggleButtonGroup>

              {/* ============================================================
                  BOTÓN DE DESCARGA DE EXCEL
              ============================================================ */}
              <Tooltip title="Descargar Excel con los datos filtrados">
                <span>
                  <MDButton
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleExportExcel}
                    disabled={isExporting || filteredData.length === 0}
                    startIcon={<DownloadIcon />}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "0.8rem",
                      fontWeight: "bold",
                      px: 2.5,
                      py: 0.8,
                      backgroundColor: "#2e7d32",
                      boxShadow: "0 2px 8px rgba(46, 125, 50, 0.2)",
                      "&:hover": {
                        backgroundColor: "#1b5e20",
                        boxShadow: "0 4px 12px rgba(46, 125, 50, 0.3)",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#e0e0e0",
                        color: "#9e9e9e",
                      },
                    }}
                  >
                    {isExporting ? "Exportando..." : "Exportar Excel"}
                  </MDButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {isDateFilterActive && filterStats && (
            <Box
              mt={2}
              pt={2}
              borderTop="1px solid #e0e0e0"
              display="flex"
              alignItems="center"
              gap={3}
              flexWrap="wrap"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Info fontSize="small" color="info" />
                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                  Filtro aplicado:
                </Typography>
              </Box>
              <Chip
                label={`Total: ${filterStats.total}`}
                size="small"
                color="default"
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Chip
                label={`Activos: ${filterStats.active}`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Chip
                label={`Completados: ${filterStats.completed}`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Chip
                label={`Placas únicas: ${filterStats.uniquePlates}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: "bold" }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: "medium" }}>
                {dateRange.startDate && dateRange.endDate
                  ? `Del ${formatDateDisplay(dateRange.startDate)} al ${formatDateDisplay(
                      dateRange.endDate
                    )}`
                  : dateRange.startDate
                  ? `Desde ${formatDateDisplay(dateRange.startDate)}`
                  : `Hasta ${formatDateDisplay(dateRange.endDate)}`}
              </Typography>
            </Box>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #e0e0e0",
          }}
        >
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={TABLE_ENTRIES}
            autoResetPage={false}
            paginationResetKey={JSON.stringify([
              searchQuery,
              filterType,
              dateRange.startDate,
              dateRange.endDate,
            ])}
            showTotalEntries={true}
            noEndBorder={true}
            canSearch={false}
            pagination={{ variant: "gradient", color: "primary" }}
            sx={{
              "& .MuiTableRow-root": {
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(13, 71, 161, 0.02)",
                },
              },
              "& .MuiTableCell-head": {
                backgroundColor: "#f5f7fa",
                fontWeight: 600,
                color: "#424242",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </Paper>

        <CloseCycleDialog
          open={openDialog}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
          selectedRecord={selectedRecord}
          isSubmitting={isSubmitting}
          submitStatus={submitStatus}
          statusMessage={statusMessage}
        />

        <CreateCycleDialog
          open={openCreateDialog}
          onClose={() => {
            if (!isCreating) {
              setOpenCreateDialog(false);
              setCreateStatus(null);
              setCreateMessage("");
            }
          }}
          onConfirm={handleCreateConfirm}
          isSubmitting={isCreating}
          submitStatus={createStatus}
          statusMessage={createMessage}
          terminalInfo={terminales?.entrada || null}
        />

        <CycleSuccessDialog
          open={openSuccessDialog}
          onClose={handleSuccessDialogClose}
          data={successData}
          type={successType}
          autoClose={true}
          autoCloseDelay={3000}
        />
      </Box>
    );
  }
);

TableSemestrales.propTypes = {
  records: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRefresh: PropTypes.func,
  onCloseCycle: PropTypes.func,
  onAddCycle: PropTypes.func,
  terminales: PropTypes.object,
  hasCreatePermission: PropTypes.bool,
  hasClosePermission: PropTypes.bool,
};

TableSemestrales.defaultProps = {
  records: [],
  loading: false,
  error: null,
  onRefresh: null,
  onCloseCycle: null,
  onAddCycle: null,
  terminales: null,
  hasCreatePermission: false,
  hasClosePermission: false,
};

export default TableSemestrales;
